import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactSVG from 'react-svg';
import * as NuacomComponent from '~/components';
import * as validate from '~/validations/form';
import * as actions from '~/store/actions';
import * as lib from '~/dependencies';
import messagesToast from '~/validations/messagesNotifications';

import { GoogleIcon, MicrosoftIcon } from '~/assets/svgIcons';
import { getPackageVersion, electron, log } from '~/helpers';
import { TWO_FACTOR_CODE_TIMEOUT } from '~/config/config';
import {
  BoxLogin,
  BackgroundFloat,
  Space,
  Version,
  Message,
  IdiomSelector,
  ThirdPartyLoginBtn,
  Divider,
  CircularProgressStyled,
} from './styles';
import * as AuthComponents from './components';
import packageJson from '../../../package.json';

const getErrorMessage = code => {
  let message;
  switch (code) {
    case 401:
      message = lib.i18n.t('screens.auth.errors.invalidUsername');
      break;
    case 'auth-invalid-secure-code':
      message = 'Invalid two-factor authentication code.';
      break;
    case 'auth-generic-error':
      message = lib.i18n.t('screens.auth.errors.generic');
      break;
    case 4001:
      message = lib.i18n.t('screens.auth.errors.passwordExpired');
      break;
    case 4002:
      message = lib.i18n.t('screens.auth.errors.nuacomLoginDisabled');
      break;
    case 4003:
      message = lib.i18n.t('screens.auth.errors.noSSOLogin');
      break;
    case 4004:
      message = lib.i18n.t('screens.auth.errors.ipRestriction');
      break;
    case 4005:
      message = lib.i18n.t('screens.auth.errors.noExtension');
      break;
    // Not API Error Codes
    case 5000:
      message = lib.i18n.t('screens.auth.errors.noPassReuse');
      break;
    default:
      message = lib.i18n.t('screens.auth.errors.generic');
  }
  return message;
};

export default function Auth({ history }) {
  // STATES
  const [loadingReset, setLoadingReset] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [errorForm, setErrorForm] = useState([]);
  const [allowResend, setAllowResend] = useState(true);
  const [timer, setTimer] = useState(null);
  const [offlineToastId, setToastId] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);

  // HOOKS
  const dispatch = useDispatch();
  const { t } = lib.useTranslation();

  // SELECTORS
  const error = useSelector(state => state.auth?.error);
  const errorCode = useSelector(state => state.auth?.errorCode);
  const isAuthenticated = useSelector(state => state.auth?.token);
  const hasExtension = useSelector(state => state.auth?.userExtension);
  const twoFactorInfo = useSelector(state => state.auth?.twoFactorInfo);
  const passExpirationReqs = useSelector(
    state => state.auth?.passExpirationReqs
  );
  const loading = useSelector(state => state.auth?.loading);
  const email = useSelector(state => state.auth?.email);
  const password = useSelector(state => state.auth?.password);
  const version = useSelector(state => state.system?.version);

  // MEMOs
  const errorMemo = useMemo(() => error, [error]);
  const errorCodeMemo = useMemo(() => errorCode, [errorCode]);

  // CONST
  const detectedLang = localStorage.getItem('i18nextLng');

  // REFs
  const protocolInterval = useRef(null);

  const handleOffline = useCallback(() => {
    // Remove previous toast.
    lib.toast.dismiss();

    const toastId = lib.ToastMessage(
      'error',
      messagesToast().AUTH.offline,
      10000,
      {
        autoClose: false,
      }
    );

    setToastId(toastId);
  }, []);

  const handleOnline = useCallback(() => {
    lib.toast.dismiss(offlineToastId);
    setToastId(null);
    lib.ToastMessage('success', messagesToast().AUTH.online, 10000);
  }, [offlineToastId]);

  // EFFECTS
  useEffect(() => {
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [handleOffline, handleOnline]);

  useEffect(() => {
    const appVersion = getPackageVersion(
      navigator.userAgent,
      packageJson.version,
      process.env.NODE_ENV
    );

    dispatch(actions.setVersion(appVersion));
    electron.changePhoneStatus('Unavailable', false);
    electron.storeLoadedWebVersion(packageJson?.version);
  }, [dispatch, version]);

  useEffect(() => {
    if (isAuthenticated && hasExtension) {
      history.push('/app/dialPad');
    }
    if (passExpirationReqs && email) {
      dispatch(actions.setAuthLoading(false));
      history.push('/auth/reset-password');
    }
  }, [
    isAuthenticated,
    hasExtension,
    passExpirationReqs,
    email,
    history,
    dispatch,
  ]);

  useEffect(() => {
    if (errorCodeMemo) {
      const errorMessage = getErrorMessage(errorCodeMemo);

      lib.ToastMessage('error', errorMessage, 5000, {
        autoClose: false,
      });
      dispatch(actions.authFail(null));
      resetThirdPartyState();
    }
  }, [errorCodeMemo, errorMemo, dispatch]);

  useEffect(() => {
    let interval = null;

    if (timer) {
      interval = setInterval(() => {
        setTimer(lastTimerCount => {
          if (lastTimerCount <= 1) {
            clearInterval(interval);
            setAllowResend(true);
          }
          return lastTimerCount - 1;
        });
      }, 1000);
    }

    // cleanup the interval on complete
    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    if (window.isElectron) {
      protocolInterval.current = setInterval(() => {
        const makeCallbyProtocol = window.ipcRenderer.sendSync(
          'makeCallbyProtocol',
          ''
        );

        if (makeCallbyProtocol && makeCallbyProtocol.value === 'Protocol') {
          try {
            const url = new URL(makeCallbyProtocol.textValue);
            const pathname = url.pathname.replace(/\//g, '');

            if (pathname === 'authenticate') {
              const provider = url.searchParams.get('provider');
              const code = url.searchParams.get('code');

              if (provider && code) {
                dispatch(actions.auth(null, null, null, provider, code));
                // resetThirdPartyState();
              }
            }
            electron.resetProtocol();
          } catch (err) {
            log.error(
              'Failure to process the custom protocol call. Reason: ',
              err
            );
          }
        }
      }, 2000);
    }

    return () => {
      if (window.isElectron) {
        clearInterval(protocolInterval.current);
        protocolInterval.current = null;
      }
    };
  }, [dispatch]);

  function getData(data) {
    const re = /^[0-9\b]+$/;
    const dtaValue = data.value.trim();

    switch (data.field) {
      case 'email':
        dispatch(actions.setAuthUserEmail(dtaValue));
        break;
      case 'password':
        dispatch(actions.setAuthUserPassword(dtaValue));
        break;
      case 'two-factor-code':
        if (dtaValue === '' || re.test(dtaValue)) {
          setTwoFactorCode(dtaValue);
        }
        break;
      default:
        break;
    }
  }

  async function validateForm() {
    try {
      lib.ToastMessage(false);
      if (twoFactorCode) {
        setLoadingReset(true);
        await validate.validateSchemaSIGNIN.validate(
          {
            email,
            password,
          },
          { abortEarly: false }
        );
        await dispatch(actions.auth(email, password, twoFactorCode));
        setLoadingReset(false);
        setTwoFactorCode('');
        resetThirdPartyState();
      } else if (!twoFactorInfo.isEnabled) {
        if (window.isElectron) {
          // Cancel any protocol request if the app is signed out.
          window.ipcRenderer.send('resetProtocol', '');
        }
        await validate.validateSchemaSIGNIN.validate(
          {
            email,
            password,
          },
          { abortEarly: false }
        );
        dispatch(actions.auth(email, password));
        resetThirdPartyState();
      }
      setErrorForm([]);
    } catch (err) {
      const result = err.inner.map(e => ({
        field: e.path,
        message: e.message,
      }));
      setTwoFactorCode('');
      setErrorForm(result);
      setLoadingReset(false);
      resetThirdPartyState();
    }
  }

  function resendCode() {
    lib.ToastMessage(false);
    if (allowResend) {
      setAllowResend(false);
      setTimer(TWO_FACTOR_CODE_TIMEOUT);
      setTwoFactorCode('');
      setTimeout(() => {
        dispatch(actions.auth(email, password));
        setErrorForm([]);
        lib.ToastMessage(
          'success',
          t('screens.auth.2fa.errInvalidCode'),
          10000
        );
      }, 300);
    }
  }

  function cancel() {
    lib.ToastMessage(false);
    setTwoFactorCode('');
    dispatch(actions.cancel2FA());
  }

  function keyPressHandler(event) {
    if (event.key === 'Enter') {
      validateForm();
    }
  }

  function onSubmit(e) {
    e.preventDefault();
  }

  function handleThirdPartyLogin(provider) {
    const url = `https://app.nuacom.ie/sso/${provider}/?origin=softphone`;

    lib.ToastMessage(false);

    if (window.isElectron) {
      electron.openExternalUrl({ url });
    } else {
      window.open(url, '_blank', 'noreferrer');
    }

    if (provider === 'google') {
      setGoogleLoading(true);
    } else {
      setMicrosoftLoading(true);
    }
  }

  function resetThirdPartyState() {
    setGoogleLoading(false);
    setMicrosoftLoading(false);
  }

  const isSSO = googleLoading || microsoftLoading;

  return (
    <div id="contentNuacom">
      {isAuthenticated && !hasExtension && (
        <NuacomComponent.ExtensionsSelector />
      )}
      <div id="mainContent">
        <BackgroundFloat />
        <IdiomSelector>
          <NuacomComponent.IdiomSelector currentLang={detectedLang} />
        </IdiomSelector>
        <form onSubmit={onSubmit}>
          <BoxLogin>
            <AuthComponents.LogoAuth />

            {twoFactorInfo?.isEnabled && !isSSO ? (
              <>
                <Space height={40} />
                <div>
                  <b>
                    {t('screens.auth.2fa.title', {
                      name: twoFactorInfo && twoFactorInfo.name,
                    })}
                  </b>
                </div>
                <Message
                  dangerouslySetInnerHTML={{
                    __html: t('screens.auth.2fa.subtitle', {
                      email: `<b>${twoFactorInfo?.email}</b>`,
                    }),
                  }}
                />
                <Space height={25} />
                <AuthComponents.InputLogin
                  name="two-factor-code"
                  label={t('screens.auth.2fa.inptLabel')}
                  data-test-id="two-factor-code"
                  value={twoFactorCode}
                  onChange={getData}
                  placeholder=""
                  validator={errorForm}
                  loading={false}
                  onKeyPress={keyPressHandler}
                />

                <Space height={25} />
                <AuthComponents.SubmitLogin
                  data-test-id="btn-two-factor"
                  onSubmit={validateForm}
                  loading={loadingReset}
                  title={t('screens.auth.2fa.btnVerify')}
                  onKeyPress={keyPressHandler}
                />

                <Space height={25} />
                <span
                  className="resend-security-code-button"
                  role="presentation"
                  onClick={resendCode}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: timer
                      ? t('screens.auth.2fa.btnResendCodeIn', {
                          timer: `<b>${timer}s</b>`,
                        })
                      : t('screens.auth.2fa.btnResendCode'),
                  }}
                />

                <Space height={15} />
                <span
                  className="cancel-security-code-button"
                  role="presentation"
                  onClick={cancel}>
                  {t('screens.auth.2fa.btnCancel')}
                </span>
              </>
            ) : (
              <>
                <Space height={42} />

                <>
                  <AuthComponents.InputLogin
                    name="email"
                    label={t('screens.auth.email.label')}
                    data-test-id="email"
                    value={email}
                    onChange={getData}
                    placeholder={t('screens.auth.email.placeholder')}
                    validator={errorForm}
                    loading={loading}
                    onKeyPress={keyPressHandler}
                  />
                  <AuthComponents.InputLogin
                    value={password}
                    name="password"
                    label={t('screens.auth.password.label')}
                    data-test-id="password"
                    onChange={getData}
                    placeholder={t('screens.auth.password.placeholder')}
                    password
                    validator={errorForm}
                    loading={loading}
                    onKeyPress={keyPressHandler}
                  />
                  <span
                    className="forgot-password-link"
                    role="presentation"
                    onClick={() => history.push('/auth/recover-password')}>
                    {t('screens.auth.recoverPass.title')}
                  </span>
                  <Space height={25} />
                  <AuthComponents.SubmitLogin
                    data-test-id="btn-login"
                    onSubmit={validateForm}
                    disabled={!email || !password || offlineToastId !== null}
                    loading={loading}
                    title={t('screens.auth.login')}
                    onKeyPress={keyPressHandler}
                  />
                  <Divider>OR</Divider>
                  <ThirdPartyLoginBtn
                    onClick={() => handleThirdPartyLogin('google')}>
                    <ReactSVG src={GoogleIcon} className="icons" />
                    <span>Sign-in with Google</span>
                    {googleLoading && <CircularProgressStyled />}
                  </ThirdPartyLoginBtn>
                  <ThirdPartyLoginBtn
                    onClick={() => handleThirdPartyLogin('microsoft')}>
                    <ReactSVG src={MicrosoftIcon} className="icons" />
                    <span>Sign-in with Microsoft</span>
                    {microsoftLoading && <CircularProgressStyled />}
                  </ThirdPartyLoginBtn>
                </>
              </>
            )}
          </BoxLogin>
        </form>
        <Version>{`v${version}`}</Version>
      </div>
    </div>
  );
}

Auth.propTypes = {
  history: lib.PropTypes.instanceOf(Object).isRequired,
};
